import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import crmActivityService, {
  type CrmActivity,
  type CrmActivityInput,
  type CrmActivityListFilters,
  type CrmActivityType,
} from '@/services/crmActivity.service';
import { LEAD_KEYS } from '@/hooks/useLeads';
import { OPPORTUNITY_KEYS } from '@/hooks/useOpportunities';

export const CRM_ACTIVITY_KEYS = {
  all: ['crm', 'activities'] as const,
  list: (filters: CrmActivityListFilters) => [...CRM_ACTIVITY_KEYS.all, 'list', filters] as const,
  summary: (filters: CrmActivityListFilters) => [...CRM_ACTIVITY_KEYS.all, 'summary', filters] as const,
  followUpSummary: (filters: CrmActivityListFilters) => [...CRM_ACTIVITY_KEYS.all, 'follow-up-summary', filters] as const,
  detail: (id: string) => [...CRM_ACTIVITY_KEYS.all, 'detail', id] as const,
  chain: (id: string) => [...CRM_ACTIVITY_KEYS.all, 'chain', id] as const,
  types: (opts?: { include_inactive?: boolean }) => ['crm', 'activity-types', opts ?? {}] as const,
};

function errMsg(e: unknown, fallback: string): string {
  const r = e as { response?: { data?: { message?: string } }; message?: string };
  return r?.response?.data?.message || r?.message || fallback;
}

/** Refresh every view an activity can appear in (registers, lead / opportunity timelines, Customer 360). */
export function invalidateCrmActivityViews(qc: QueryClient, row?: Partial<CrmActivity> | null): void {
  qc.invalidateQueries({ queryKey: CRM_ACTIVITY_KEYS.all });
  if (row?.lead_id) {
    qc.invalidateQueries({ queryKey: LEAD_KEYS.activities(row.lead_id) });
    qc.invalidateQueries({ queryKey: LEAD_KEYS.detail(row.lead_id) });
  }
  if (row?.opportunity_id) {
    qc.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.activities(row.opportunity_id) });
  }
  if (row?.customer_id) {
    qc.invalidateQueries({ queryKey: ['customer-360', row.customer_id] });
  }
}

export function useCrmActivities(filters: CrmActivityListFilters, enabled = true) {
  return useQuery({
    queryKey: CRM_ACTIVITY_KEYS.list(filters),
    queryFn: () => crmActivityService.list(filters),
    enabled,
  });
}

export function useCrmActivitySummary(filters: CrmActivityListFilters, enabled = true) {
  return useQuery({
    queryKey: CRM_ACTIVITY_KEYS.summary(filters),
    queryFn: () => crmActivityService.summary(filters),
    enabled,
  });
}

export function useCrmFollowUpSummary(filters: CrmActivityListFilters) {
  return useQuery({
    queryKey: CRM_ACTIVITY_KEYS.followUpSummary(filters),
    queryFn: () => crmActivityService.followUpSummary(filters),
  });
}

export function useCrmActivity(id: string | undefined) {
  return useQuery({
    queryKey: CRM_ACTIVITY_KEYS.detail(id ?? ''),
    queryFn: () => crmActivityService.getById(id!),
    enabled: !!id,
  });
}

export function useCrmActivityChain(id: string | undefined) {
  return useQuery({
    queryKey: CRM_ACTIVITY_KEYS.chain(id ?? ''),
    queryFn: () => crmActivityService.getChain(id!),
    enabled: !!id,
  });
}

export function useCrmActivityTypes(opts?: { include_inactive?: boolean }) {
  return useQuery({
    queryKey: CRM_ACTIVITY_KEYS.types(opts),
    queryFn: () => crmActivityService.listTypes(opts),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateCrmActivity() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: (body: CrmActivityInput) => crmActivityService.create(body),
    onSuccess: (row) => {
      invalidateCrmActivityViews(qc, row);
      message.success('Activity logged');
    },
    onError: (e) => message.error(errMsg(e, 'Failed to log activity')),
  });
}

export function useUpdateCrmActivity() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<CrmActivityInput> }) => crmActivityService.update(id, body),
    onSuccess: (row) => {
      invalidateCrmActivityViews(qc, row);
      message.success('Activity updated');
    },
    onError: (e) => message.error(errMsg(e, 'Failed to update activity')),
  });
}

export function useDeleteCrmActivity() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: (row: CrmActivity) => crmActivityService.remove(row.id),
    onSuccess: (_, row) => {
      invalidateCrmActivityViews(qc, row);
      message.success('Activity deleted');
    },
    onError: (e) => message.error(errMsg(e, 'Failed to delete activity')),
  });
}

export function useCompleteCrmFollowUp() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof crmActivityService.complete>[1];
    }) => crmActivityService.complete(id, body),
    onSuccess: (row) => {
      invalidateCrmActivityViews(qc, row);
      message.success(row?.follow_up_status === 'Cancelled' ? 'Follow-up cancelled' : 'Follow-up completed');
    },
    onError: (e) => message.error(errMsg(e, 'Failed to update follow-up')),
  });
}

export function useRescheduleCrmFollowUp() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({
      id,
      follow_up_date,
      follow_up_time,
      reason,
    }: {
      id: string;
      follow_up_date: string;
      follow_up_time?: string | null;
      reason?: string;
    }) => crmActivityService.reschedule(id, { follow_up_date, follow_up_time, reason }),
    onSuccess: (row) => {
      invalidateCrmActivityViews(qc, row);
      message.success('Follow-up rescheduled');
    },
    onError: (e) => message.error(errMsg(e, 'Failed to reschedule follow-up')),
  });
}

export function useReassignCrmFollowUp() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({ id, follow_up_owner }: { id: string; follow_up_owner: string }) =>
      crmActivityService.reassign(id, { follow_up_owner }),
    onSuccess: (row) => {
      invalidateCrmActivityViews(qc, row);
      message.success('Follow-up reassigned');
    },
    onError: (e) => message.error(errMsg(e, 'Failed to reassign follow-up')),
  });
}

export function useSaveCrmActivityType() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Partial<CrmActivityType> }) =>
      id ? crmActivityService.updateType(id, body) : crmActivityService.createType(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'activity-types'] });
      message.success('Activity type saved');
    },
    onError: (e) => message.error(errMsg(e, 'Failed to save activity type')),
  });
}
